import {
  Calculator,
  Clock3,
  ScanLine,
  CornerDownLeft,
  Square,
  FileCode2,
  FilePenLine,
  FileSliders,
  Folder,
  FolderSearch,
  Globe2,
  Mail,
  MessageCircle,
  Search,
  Server,
  Upload,
  Wrench,
} from "lucide-react";
import { t } from "../../i18n";
import { commandActions } from "./AsyncCommandResult.utils";
import { useEffect, useRef, useState, type SyntheticEvent } from "react";

export function useInteractionDisclosure(interactionNeeded: boolean) {
  const [open, setOpen] = useState(interactionNeeded);
  const automaticToggle = useRef(false);
  const automaticallyOpened = useRef(interactionNeeded);
  const manuallyOpened = useRef(false);
  const previousInteractionNeeded = useRef(interactionNeeded);

  useEffect(() => {
    const interactionStarted =
      interactionNeeded && !previousInteractionNeeded.current;
    const interactionEnded =
      !interactionNeeded && previousInteractionNeeded.current;

    if (interactionStarted && !open) {
      automaticallyOpened.current = true;
      automaticToggle.current = true;
      setOpen(true);
    } else if (
      interactionEnded &&
      automaticallyOpened.current &&
      !manuallyOpened.current
    ) {
      automaticToggle.current = true;
      setOpen(false);
      automaticallyOpened.current = false;
    }

    previousInteractionNeeded.current = interactionNeeded;
  }, [interactionNeeded, open]);

  const onToggle = (event: SyntheticEvent<HTMLDetailsElement>) => {
    const nextOpen = event.currentTarget.open;
    setOpen(nextOpen);
    if (automaticToggle.current) {
      automaticToggle.current = false;
      return;
    }
    if (nextOpen) manuallyOpened.current = true;
  };

  return { open, onToggle };
}

export function toolPresentation(name: string, displayName?: string) {
  if (Object.hasOwn(commandActions, name)) {
    const Icon = name.endsWith("_await")
      ? Clock3
      : name.endsWith("_inspect")
        ? ScanLine
        : name.endsWith("_stdin")
          ? CornerDownLeft
          : Square;
    return {
      label: displayName || t(commandActions[name]),
      icon: <Icon size={14} aria-hidden="true" />,
      iconClass: "bg-sky-100 text-sky-700 dark:bg-sky-950/50 dark:text-sky-300",
      cardClass:
        "border-stone-200 bg-stone-50/70 dark:border-white/10 dark:bg-white/[0.03]",
    };
  }
  if (name === "calculate")
    return {
      label: displayName || "Calculate",
      icon: <Calculator size={14} />,
      iconClass: "bg-sky-100 text-sky-700 dark:bg-sky-950/50 dark:text-sky-300",
      cardClass:
        "border-sky-200 bg-sky-50/30 dark:border-sky-900/50 dark:bg-sky-950/10",
    };
  if (name === "ollama_web_search" || name === "tavily_web_search")
    return {
      label: displayName || "Web search",
      icon: <Search size={14} />,
      iconClass:
        "bg-lumen-100 text-lumen-700 dark:bg-lumen-950/50 dark:text-lumen-300",
      cardClass:
        "border-lumen-200 bg-lumen-50/30 dark:border-lumen-900/50 dark:bg-lumen-950/10",
    };
  if (name === "ollama_web_fetch" || name === "tavily_web_fetch")
    return {
      label: displayName || "Read webpage",
      icon: <Globe2 size={14} />,
      iconClass:
        "bg-indigo-100 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300",
      cardClass:
        "border-indigo-200 bg-indigo-50/30 dark:border-indigo-900/50 dark:bg-indigo-950/10",
    };
  if (name === "send_email_notification")
    return {
      label: displayName || "Send email",
      icon: <Mail size={14} />,
      iconClass:
        "bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300",
      cardClass:
        "border-amber-200 bg-amber-50/30 dark:border-amber-900/50 dark:bg-amber-950/10",
    };
  if (name === "send_whatsapp_message")
    return {
      label: displayName || "Send WhatsApp message",
      icon: <MessageCircle size={14} />,
      iconClass:
        "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300",
      cardClass:
        "border-emerald-200 bg-emerald-50/30 dark:border-emerald-900/50 dark:bg-emerald-950/10",
    };
  if (name === "agent_workspace_shell")
    return {
      label: displayName || "Workspace shell",
      icon: <Wrench size={14} />,
      iconClass:
        "bg-stone-200 text-stone-800 dark:bg-white/10 dark:text-stone-200",
      cardClass:
        "border-stone-300 bg-stone-100/50 dark:border-white/10 dark:bg-white/[0.03]",
    };
  if (name === "agent_workspace_upload_artifact")
    return {
      label: displayName || "Upload artifact",
      icon: <Upload size={14} />,
      iconClass:
        "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300",
      cardClass:
        "border-emerald-200 bg-emerald-50/30 dark:border-emerald-900/50 dark:bg-emerald-950/10",
    };
  if (["workspace_list", "workspace_glob"].includes(name))
    return {
      label:
        displayName ||
        (name === "workspace_list" ? "List workspace" : "Find workspace paths"),
      icon:
        name === "workspace_list" ? (
          <Folder size={14} />
        ) : (
          <FolderSearch size={14} />
        ),
      iconClass:
        "bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300",
      cardClass:
        "border-amber-200 bg-amber-50/30 dark:border-amber-900/50 dark:bg-amber-950/10",
    };
  if (
    [
      "workspace_read",
      "workspace_edit",
      "workspace_overwrite",
      "workspace_write",
      "workspace_patch",
    ].includes(name)
  )
    return {
      label:
        displayName ||
        (name === "workspace_read"
          ? "Read workspace file"
          : name === "workspace_overwrite" ||
              name === "workspace_write" ||
              name === "workspace_edit"
            ? "Write workspace file"
            : "Update workspace file"),
      icon:
        name === "workspace_read" ? (
          <FileCode2 size={14} />
        ) : (
          <FilePenLine size={14} />
        ),
      iconClass: "bg-sky-100 text-sky-700 dark:bg-sky-950/50 dark:text-sky-300",
      cardClass:
        "border-sky-200 bg-sky-50/30 dark:border-sky-900/50 dark:bg-sky-950/10",
    };
  if (name === "workspace_rg")
    return {
      label: displayName || "Search workspace files",
      icon: <Search size={14} />,
      iconClass:
        "bg-violet-100 text-violet-700 dark:bg-violet-950/50 dark:text-violet-300",
      cardClass:
        "border-violet-200 bg-violet-50/30 dark:border-violet-900/50 dark:bg-violet-950/10",
    };
  if (name.startsWith("proxmox_"))
    return {
      label: displayName || name.replace("proxmox_", "").replace(/_/g, " "),
      icon: <Server size={14} />,
      iconClass:
        "bg-indigo-100 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300",
      cardClass:
        "border-indigo-200 bg-indigo-50/30 dark:border-indigo-900/50 dark:bg-indigo-950/10",
    };
  if (name === "document_studio")
    return {
      label: displayName || "Document Studio",
      icon: <FileSliders size={14} />,
      iconClass:
        "bg-rose-100 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300",
      cardClass:
        "border-rose-200 bg-rose-50/30 dark:border-rose-900/50 dark:bg-rose-950/10",
    };
  return {
    label:
      displayName ||
      name
        .replace(/_/g, " ")
        .replace(/\b\w/g, (character) => character.toUpperCase()),
    icon: <Wrench size={14} />,
    iconClass:
      "bg-violet-100 text-violet-700 dark:bg-violet-950/50 dark:text-violet-300",
    cardClass:
      "border-stone-200 bg-stone-50/70 dark:border-white/10 dark:bg-white/[0.03]",
  };
}
